import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * API client — see ADR-0009 (auth) and ADR-0010 (REST conventions).
 *
 * Responsibilities:
 *   - Send credentials (refresh cookie) on every request — `withCredentials: true`.
 *   - Attach the in-memory access token to `Authorization` on every request.
 *   - On 401, attempt exactly one refresh round-trip; queue concurrent
 *     requests behind the same in-flight refresh promise so we don't
 *     stampede the `/refresh` endpoint.
 *   - On refresh failure, clear the token and let the caller redirect to
 *     `/login`.
 *
 * The access token lives in memory only (never `localStorage`) so XSS can't
 * exfiltrate it across page loads — refresh on reload happens via the
 * httpOnly cookie + `/me` query.
 */

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code?: string;
  instance?: string;
  errors?: Array<{ field: string; code: string; message?: string }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;

  constructor(message: string, status: number, problem?: ProblemDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

type TokenListener = (token: string | null) => void;

class AccessTokenStore {
  private token: string | null = null;
  private listeners = new Set<TokenListener>();

  get(): string | null {
    return this.token;
  }

  set(token: string | null): void {
    this.token = token;
    for (const listener of this.listeners) listener(token);
  }

  subscribe(listener: TokenListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const accessTokenStore = new AccessTokenStore();

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * Endpoints that must NOT trigger the 401 → refresh → retry loop:
 *   - `/auth/login`     — wrong creds yield 401; we surface it to the form.
 *   - `/auth/refresh`   — itself the refresh call; a 401 here ends the session.
 *   - `/auth/logout`    — server-side revoke; 401 is fine, no retry needed.
 *
 * The check is anchored (regex) rather than substring `includes()` so that
 * future routes like `/admin/auth-log` or `/users/auth-history` don't get
 * silently treated as auth endpoints (audit D-15).
 */
const REFRESH_EXEMPT_AUTH_ENDPOINTS = /(?:^|\/)auth\/(?:login|refresh|logout)(?:[/?#]|$)/;

export function isRefreshExemptAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return REFRESH_EXEMPT_AUTH_ENDPOINTS.test(url);
}

/**
 * Build the singleton axios instance. Exported as a factory so tests can
 * construct isolated instances with their own base URL / interceptor state.
 */
export function createApiClient(baseURL = '/api/v1'): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    // Axios default serialization pro arrays je `?param[]=a&param[]=b`,
    // ale BE Zod schémata (např. `listPartnersQuery.tags`/`status`) čekají
    // **repeat format** bez závorek: `?param=a&param=b`. Bez tohoto
    // serializátoru by FE filtery na multi-value query (tags, status)
    // tiše neaplikovaly server-side filter — BE viděl key `param[]` který
    // se v jeho schémě neexistuje a defaultoval na undefined (= bez filteru).
    // Opraveno #72180 po nálezu chip toggle bugu na /partners.
    paramsSerializer: {
      indexes: null,
    },
  });

  let refreshInFlight: Promise<string | null> | null = null;
  let onSessionExpired: (() => void) | null = null;

  /**
   * Perform a single `/auth/refresh` round-trip, de-duplicated across all
   * callers (the 401 interceptor AND the auth provider's bootstrap /
   * end-impersonation refresh) so a tab never fires two concurrent refreshes.
   *
   * This matters for session longevity: the backend rotates the refresh token
   * on every call, and two concurrent refreshes from the same tab would make
   * the second present an already-rotated token. With a short server-side
   * grace window that's tolerated, but de-duplicating here removes the race
   * entirely — concurrent callers share one rotation and one new token.
   *
   * On success the new access token is stored and returned. On failure the
   * token is cleared and `onSessionExpired` fires (so the provider can flip to
   * anonymous); the rejection is re-thrown for the caller that needs it.
   */
  function refreshSession(): Promise<string | null> {
    if (!refreshInFlight) {
      refreshInFlight = client
        .post<{ access_token: string }>('/auth/refresh')
        .then((res) => {
          const next = res.data.access_token;
          accessTokenStore.set(next);
          return next;
        })
        .catch((refreshErr) => {
          accessTokenStore.set(null);
          onSessionExpired?.();
          throw refreshErr;
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  }

  client.interceptors.request.use((config) => {
    const token = accessTokenStore.get();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ProblemDetails>) => {
      const original = error.config as RetryableRequest | undefined;
      const status = error.response?.status;

      const isAuthEndpoint = isRefreshExemptAuthEndpoint(original?.url);

      if (status === 401 && original && !original._retried && !isAuthEndpoint) {
        original._retried = true;
        try {
          const token = await refreshSession();
          if (token) {
            original.headers.set('Authorization', `Bearer ${token}`);
            return client.request(original);
          }
        } catch {
          // Fall through to the rejection below — caller sees an ApiError.
        }
      }

      throw toApiError(error);
    },
  );

  // Side-channel for the auth provider to register its logout hook.
  (client as AxiosInstance & { onSessionExpired: (cb: () => void) => void }).onSessionExpired = (
    cb,
  ) => {
    onSessionExpired = cb;
  };

  // Side-channel exposing the de-duplicated refresh so the auth provider's
  // bootstrap / end-impersonation paths share the same in-flight promise as
  // the 401 interceptor (no concurrent `/auth/refresh` from one tab).
  (client as AxiosInstance & { refreshSession: () => Promise<string | null> }).refreshSession =
    refreshSession;

  return client;
}

function toApiError(error: AxiosError<ProblemDetails>): ApiError {
  const status = error.response?.status ?? 0;
  const problem = error.response?.data;
  const message = problem?.title ?? error.message ?? 'Request failed';
  return new ApiError(message, status, problem);
}

export const api = createApiClient();

/** Helper for typed POST/GET with the singleton client. */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await api.request<T>(config);
  return res.data;
}

/**
 * Prune a query-param bag for list endpoints. Drops `undefined`, `null`,
 * empty strings and empty arrays so the URL stays clean (no `?q=` or
 * `?kind=` orphans). **Keeps `false` and `0`** — a flag that should be
 * omitted when false is passed as `flag || undefined` by the caller, so a
 * `false` boolean is never serialized into the query string (which the
 * backend's `z.coerce.boolean` would read back as `true`).
 *
 * Replaces the per-endpoint `if (params.x …) query.x = …` ladders that every
 * list API client repeated identically.
 */
export function buildQueryParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Refresh the session through the singleton client's de-duplicated channel —
 * shares the in-flight promise with the 401 interceptor so the auth provider's
 * bootstrap and end-impersonation paths never race a second `/auth/refresh`
 * against an interceptor-triggered one. Resolves to the new access token (also
 * stored in `accessTokenStore`) or rejects if the refresh fails.
 */
export function refreshSession(): Promise<string | null> {
  return (api as AxiosInstance & { refreshSession: () => Promise<string | null> }).refreshSession();
}
